import BugReportIcon from '@mui/icons-material/BugReport'
import { IconButton, Modal, Paper, Stack, Typography, useTheme } from '@mui/material'
import * as colors from '@mui/material/colors'
import { Node as XYNode, NodeProps as XYNodeProps } from '@xyflow/react'
import { memo, useEffect, useRef, useState } from 'react'
import affirm from '../../../core/Affirm'
import algo from '../../../core/Algo'
import { FlowAnimationType, IFlowAnimation, IFlowAnimationGroup } from '../../../definitions/FlowAnimation'
import { INode, NodeType } from '../../../definitions/Nodes'
import EventsEngine from '../../../engines/EventsEngine'
import Helper from '../../../helpers/Helper'
import NodeManager from '../../../managers/NodeManager'
import { Queue } from '../../../managers/QueueManager'
import { useAppSelector } from '../../../store/Hooks'
import JSONEditor from '../../editor/JSONEditor'
import BaseNodeUI from './BaseNodeUI'
import CustomInputHandle from './CustomInputHandle'
import CustomOutputHandle from './CustomOutputHandle'
import NodesIcon from './NodesIcon'

export type P = XYNode<{
    node: INode
}, NodeType>

const CustomNodeUI = ({ data }: XYNodeProps<P>) => {
    affirm(data, 'Invalid data parameter passed to node')
    affirm(data.node, 'Invalid node in data parameter passed to node')

    const [openDebugModal, setOpenDebugModal] = useState(false)
    const debugMode = useAppSelector(s => s.session.debugMode)

    const color = () => {
        switch (node.type) {
            case 'xfrm_sql': return colors.purple[300]
            case 'var_dataframe': return colors.amber[300]
            case 'xfrm_reduce': return colors.orange[600]
            case 'xfrm_dataframeops': return colors.orange[300]
            case 'xfrm_code': return colors.cyan[300]
            case 'out_csv': return colors.green[300]
            case 'stmt_settlement': return colors.lime[300]
            case 'ds_redshift':
            case 'ds_dynamo': return colors.indigo[300]
            case 'var_variable': return colors.blue[300]
            default: return 'var(--icon-color)'
        }
    }

    const { node } = data
    const theme = useTheme()

    const description = NodeManager.getDescription(node.type)
    affirm(description, `No descriptor found for node "${node.type}"`)

    const [uiStatus, setUIStatus] = useState<FlowAnimationType>('waiting')
    const animationQueue = useRef<Queue<IFlowAnimation>>(new Queue())

    useEffect(() => {
        EventsEngine.subscribe('flow-animation', 'CustomNodeUI' + node.uuid, (args: IFlowAnimationGroup) => {
            const animations = args?.animations.filter(x => x.nodeId === node.uuid && !algo.hasVal(x.edgeId))
            if (animations && animations.length > 0) {
                for (let i = 0; i < animations.length; i++) {
                    const anim = animations[i]

                    animationQueue.current.enqueue(anim)
                    setTimeout(() => {
                        const toPlay = animationQueue.current.dequeue()
                        setUIStatus(toPlay.animation)
                    }, anim.delay ?? 0)
                }
            }
        })

        // This work but I don't know if is the right way
        EventsEngine.subscribe('query-cache-stale', 'CustomNodeUI' + node.uuid, () => {
            animationQueue.current.clear()
            setUIStatus('waiting')
        })

        return () => {
            EventsEngine.unsubscribe('flow-animation', 'CustomNodeUI' + node.uuid)
            EventsEngine.unsubscribe('query-cache-stale', 'CustomNodeUI' + node.uuid)
        }
    }, [node.uuid])

    const renderHeader = () => {
        switch (node.type) {
            case 'start': return (
                <div style={{ height: '100%', width: '100%', flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <NodesIcon nodeType={node.type} size={24} />
                </div>
            )
            default: return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: 'calc(var(--unit) * 2)', padding: 'var(--unit)' }}>
                    <NodesIcon nodeType={node.type} size={25} />
                    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Typography color={theme.palette.text.disabled} fontSize={10} textTransform='uppercase'>
                            {description.name}
                        </Typography>
                        <Typography
                            noWrap>
                            {node.name}
                        </Typography>
                    </div>
                </div>
            )
        }
    }

    return (
        <BaseNodeUI node={node} animation={uiStatus}>
            <div style={{
                borderLeft: `${node.type === 'start' ? undefined : `var(--unit) solid ${color()}`}`,
                padding: 'var(--unit)',
                borderTopLeftRadius: 'calc(var(--unit) - 1px)',
                borderBottomLeftRadius: 'calc(var(--unit) - 1px)'
            }}>
                {(Helper.isDev() || debugMode) && (
                    <div style={{ position: 'absolute', top: node.type === 'start' ? '-4px' : '0px', right: node.type === 'start' ? '-4px' : '0px' }}>
                        <IconButton size='small' color='primary' onClick={() => setOpenDebugModal(true)}>
                            <BugReportIcon sx={{ fontSize: 10 }} />
                        </IconButton>
                    </div>
                )}
                {renderHeader()}
                {description.acceptedInputs.map((x, i) => {
                    return (
                        <CustomInputHandle
                            key={x.argumentKey}
                            acceptedInput={x}
                            index={i}
                            inputsCount={description.acceptedInputs.length}
                        />
                    )
                })}
                {description.outputs.map((x, i) => {
                    return (
                        <CustomOutputHandle
                            key={x.key}
                            index={i}
                            output={x}
                            outputsCount={description.outputs.length}
                        />
                    )
                })}
            </div>
            {(Helper.isDev() || debugMode) && (
                <Modal open={openDebugModal} onClose={() => setOpenDebugModal(false)} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Paper sx={{ display: 'flex', flexDirection: 'column', width: '50vw', height: '50vh', padding: 2, overflow: 'hidden' }}>
                        <Stack spacing={1} flex={1} overflow='hidden'>
                            <Typography>Node info:</Typography>
                            <Stack flex={1} overflow='hidden'>
                                <JSONEditor value={node} readOnly />
                            </Stack>
                        </Stack>
                    </Paper>
                </Modal>
            )}
        </BaseNodeUI>
    )
}

export default memo(CustomNodeUI)
